import { join } from 'node:path';
import type { CacheState } from '@wix/cli-fs-cache';
import { VERSION_CACHE_FILENAME } from '@wix/cli-core-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { createFsCache, getFsCacheFilePath } from '@wix/cli-fs-cache';
import { getTestOverrides } from '@wix/cli-test-overrides';
import pTimeout from 'p-timeout';
import fetchPackageJson from 'package-json';
import { match } from 'variant';
import { z } from 'zod';
import type { PackageJson } from './types.js';

const versionCacheFileSchema = z.object({
  deprecated: z.string().optional(),
  latestVersion: z.string(),
});

type VersionCacheFile = z.TypeOf<typeof versionCacheFileSchema>;

export async function fetchPackageDetails(packageJson: PackageJson) {
  let versionCache: VersionCacheFile | null = null;

  try {
    const { name: packageName, version: packageVersion } = packageJson;

    const cliCache = createFsCache({
      filePath: getFsCacheFilePath(
        join(__dirname, '..'),
        VERSION_CACHE_FILENAME
      ),
      ttlInHours: 24,
    });

    const cacheState = await cliCache.get();

    versionCache = matchVersionState(cacheState);

    if (!versionCache) {
      const cliPackageDetails = await fetchPackageDataFromNpm({
        packageName,
        packageVersion,
      });

      versionCache = {
        deprecated: cliPackageDetails.deprecated,
        latestVersion: cliPackageDetails.latestVersion,
      };

      await cliCache.set(versionCache);
    }

    return versionCache;
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToFetchPackageDetails(),
      cause: e,
    });
  }
}

function matchVersionState(cacheState: CacheState) {
  return match(cacheState, {
    Exists: ({ data: versionCache }) => {
      const cache = versionCacheFileSchema.safeParse(versionCache);
      return cache.success ? cache.data : null;
    },
    NotExists: () => null,
  });
}

async function fetchPackageDataFromNpm({
  packageName,
  packageVersion,
}: {
  packageName: string;
  packageVersion: string;
}) {
  const testOverrides = getTestOverrides();

  const packageJsonPromise = fetchPackageJson(packageName, {
    registryUrl: testOverrides.registryUrl,
    version: packageVersion,
    allVersions: true,
  });

  const result = await pTimeout(packageJsonPromise, {
    message: 'Reached timeout while fetching Wix CLI package details',
    milliseconds: 5000,
  });

  if (result instanceof Error) {
    throw result;
  }

  return {
    deprecated: result.versions[packageVersion]?.deprecated,
    latestVersion: result['dist-tags'].latest,
  };
}
